import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { documentsAPI, databaseAPI, extractionAPI, matchingAPI, ocrResultsAPI } from '../../services/api';
import socketService from '../../services/socketService';
import { getFile } from '../../utils/fileStorage';

// Async thunks
export const extractValues = createAsyncThunk(
  'documents/extractValues',
  async ({ files, loanId, preview = true, selectedDocuments = null }, { rejectWithValue }) => {
    try {
      console.log(`🔍 Extracting values from documents (preview: ${preview})...`);
      if (selectedDocuments) {
        console.log(`🔍 Using ${selectedDocuments.length} selected documents`);
      }
      
      // Always use file upload endpoint
      const uploadFiles = {
        // Pass both singular and array files
        proposalFile: files.proposalFile?.isUploadedFile ? getFile(files.proposalFile.fileKey) : files.proposalFile,
        noteFile: files.noteFile?.isUploadedFile ? getFile(files.noteFile.fileKey) : files.noteFile,
        securityFile: files.securityFile?.isUploadedFile ? getFile(files.securityFile.fileKey) : files.securityFile,
        // Pass all files arrays for multi-file support
        proposalFiles: files.proposalFiles || [],
        noteFiles: files.noteFiles || [],
        securityFiles: files.securityFiles || [],
        additionalFiles: files.additionalFiles || [],
        archiveFiles: files.archiveFiles || [],
        checklistFiles: files.checklistFiles || []
      };
      
      const response = await extractionAPI.extractUpload(uploadFiles, loanId, preview, selectedDocuments);
      
      const result = response.data;
      
      if (!result.success) {
        throw new Error('Extraction pipeline failed');
      }
      
      console.log(`✅ Extraction complete: ${result.analysis?.perfect_matches || 0} perfect matches`);
      console.log(`📊 Preview mode: ${result.preview_mode ? 'ON' : 'OFF'}`);
      
      return {
        extraction_result: result.extraction_result,
        match_result: result.match_result,
        analysis: result.analysis,
        save_result: result.save_result,
        loan_id: result.loan_id,
        preview_mode: result.preview_mode,
        documents_processed: result.documents_processed,
        success: true
      };
      
    } catch (error) {
      console.error('Error in extractValues:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Error in extraction pipeline');
    }
  }
);

// New action for saving preview results
export const savePreviewResults = createAsyncThunk(
  'documents/savePreviewResults',
  async ({ matchedResults, loanId }, { rejectWithValue }) => {
    try {
      console.log(`🔍 Saving preview results to database for loan ${loanId}...`);
      
      const response = await extractionAPI.savePreview(matchedResults, loanId);
      const result = response.data;
      
      if (!result.success) {
        throw new Error('Save preview failed');
      }
      
      console.log(`✅ Successfully saved ${result.saved_fields} fields to database`);
      
      return {
        saved_fields: result.saved_fields,
        loan_id: result.loan_id,
        message: result.message,
        success: true
      };
      
    } catch (error) {
      console.error('Error saving preview results:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Error saving preview results');
    }
  }
);

export const analyzeDocuments = createAsyncThunk(
  'documents/analyzeDocuments',
  async (files, { rejectWithValue }) => {
    try {
      const response = await documentsAPI.analyzeDocuments(files);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error analyzing documents');
    }
  }
);

export const processPDF = createAsyncThunk(
  'documents/processPDF',
  async (file, { rejectWithValue }) => {
    try {
      const response = await documentsAPI.processPDF(file);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error processing PDF');
    }
  }
);

export const autoPopulateFiles = createAsyncThunk(
  'documents/autoPopulateFiles',
  async (directoryPath, { rejectWithValue }) => {
    try {
      const response = await documentsAPI.autoPopulateFiles(directoryPath);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error auto-populating files');
    }
  }
);

// Helper function to extract date from filename
const extractDateFromFilename = (filename) => {
  // Look for common date patterns: YYYY-MM-DD, MM-DD-YYYY, MM/DD/YYYY, etc.
  const datePatterns = [
    /(\d{4})-(\d{2})-(\d{2})/, // YYYY-MM-DD
    /(\d{2})-(\d{2})-(\d{4})/, // MM-DD-YYYY
    /(\d{2})\/(\d{2})\/(\d{4})/, // MM/DD/YYYY
    /(\d{4})(\d{2})(\d{2})/, // YYYYMMDD
    /(\d{2})(\d{2})(\d{4})/, // MMDDYYYY
  ];
  
  for (const pattern of datePatterns) {
    const match = filename.match(pattern);
    if (match) {
      let year, month, day;
      if (pattern === /(\d{4})-(\d{2})-(\d{2})/ || pattern === /(\d{4})(\d{2})(\d{2})/) {
        // YYYY-MM-DD or YYYYMMDD
        year = parseInt(match[1]);
        month = parseInt(match[2]);
        day = parseInt(match[3]);
      } else {
        // MM-DD-YYYY, MM/DD/YYYY, or MMDDYYYY
        month = parseInt(match[1]);
        day = parseInt(match[2]);
        year = parseInt(match[3]);
      }
      
      // Create date object (month is 0-indexed in JavaScript)
      const date = new Date(year, month - 1, day);
      return date.getTime();
    }
  }
  
  // If no date found, use file modification time or current time
  return Date.now();
};

const initialState = {
  // File uploads - now arrays to support multiple files per type
  proposalFiles: [],
  noteFiles: [],
  securityFiles: [],
  additionalFiles: [], // Files that don't match any document type
  archiveFiles: [],
  checklistFiles: [],
  
  // Legacy single file support (for backward compatibility)
  proposalFile: null,
  noteFile: null,
  securityFile: null,
  
  // Results
  extractionResults: null,
  analysisResults: null,
  
  // Active loan tracking
  activeLoanId: null,
  
  // Auto-populate
  autoPopulatedFiles: null,
  largeFiles: [], // Files >10MB that need manual upload
  
  // Loading states
  loading: {
    extract: false,
    analyze: false,
    processPDF: false,
    autoPopulate: false,
    deleteResults: false,
    previewExtraction: false, // Track preview extraction separately
  },
  
  // Error states
  errors: {
    extract: null,
    analyze: null,
    processPDF: null,
    autoPopulate: null,
  }
};

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setProposalFile: (state, action) => {
      state.proposalFile = action.payload;
    },
    setNoteFile: (state, action) => {
      state.noteFile = action.payload;
    },
    setSecurityFile: (state, action) => {
      state.securityFile = action.payload;
    },
    
    // Reducers for setting entire file arrays
    setProposalFiles: (state, action) => {
      state.proposalFiles = action.payload;
      state.proposalFile = action.payload[0] || null; // Set first as primary
    },
    setNoteFiles: (state, action) => {
      state.noteFiles = action.payload;
      state.noteFile = action.payload[0] || null; // Set first as primary
    },
    setSecurityFiles: (state, action) => {
      state.securityFiles = action.payload;
      state.securityFile = action.payload[0] || null; // Set first as primary
    },
    setAdditionalFiles: (state, action) => {
      state.additionalFiles = action.payload;
    },
    setArchiveFiles: (state, action) => {
      state.archiveFiles = action.payload;
    },
    setChecklistFiles: (state, action) => {
      state.checklistFiles = action.payload;
    },
    
    // New reducers for multiple files with date-based priority
    addProposalFile: (state, action) => {
      const newFile = action.payload;
      state.proposalFiles.push(newFile);
      // Sort by date (newest first) and keep the newest as the primary file
      state.proposalFiles.sort((a, b) => {
        const dateA = extractDateFromFilename(a.name || a.fileName || '');
        const dateB = extractDateFromFilename(b.name || b.fileName || '');
        return dateB - dateA; // Newest first
      });
      state.proposalFile = state.proposalFiles[0]; // Set newest as primary
    },
    addNoteFile: (state, action) => {
      const newFile = action.payload;
      state.noteFiles.push(newFile);
      // Sort by date (newest first) and keep the newest as the primary file
      state.noteFiles.sort((a, b) => {
        const dateA = extractDateFromFilename(a.name || a.fileName || '');
        const dateB = extractDateFromFilename(b.name || b.fileName || '');
        return dateB - dateA; // Newest first
      });
      state.noteFile = state.noteFiles[0]; // Set newest as primary
    },
    addSecurityFile: (state, action) => {
      const newFile = action.payload;
      state.securityFiles.push(newFile);
      // Sort by date (newest first) and keep the newest as the primary file
      state.securityFiles.sort((a, b) => {
        const dateA = extractDateFromFilename(a.name || a.fileName || '');
        const dateB = extractDateFromFilename(b.name || b.fileName || '');
        return dateB - dateA; // Newest first
      });
      state.securityFile = state.securityFiles[0]; // Set newest as primary
    },
    addAdditionalFile: (state, action) => {
      const newFile = action.payload;
      state.additionalFiles.push(newFile);
      // Sort by date (newest first)
      state.additionalFiles.sort((a, b) => {
        const dateA = extractDateFromFilename(a.name || a.fileName || '');
        const dateB = extractDateFromFilename(b.name || b.fileName || '');
        return dateB - dateA; // Newest first
      });
    },
    addArchiveFile: (state, action) => {
      const newFile = action.payload;
      state.archiveFiles.push(newFile);
      // Sort by date (newest first)
      state.archiveFiles.sort((a, b) => {
        const dateA = extractDateFromFilename(a.name || a.fileName || '');
        const dateB = extractDateFromFilename(b.name || b.fileName || '');
        return dateB - dateA; // Newest first
      });
    },
    addChecklistFile: (state, action) => {
      const newFile = action.payload;
      state.checklistFiles.push(newFile);
      // Sort by date (newest first)
      state.checklistFiles.sort((a, b) => {
        const dateA = extractDateFromFilename(a.name || a.fileName || '');
        const dateB = extractDateFromFilename(b.name || b.fileName || '');
        return dateB - dateA; // Newest first
      });
    },
    
    // Actions for moving files between document types
    moveFile: (state, action) => {
      const { file, fromType, toType, fromIndex } = action.payload;
      
      // Remove file from source array
      let sourceArray = [];
      let targetArray = [];
      
      switch (fromType) {
        case 'proposal':
          sourceArray = state.proposalFiles;
          break;
        case 'note':
          sourceArray = state.noteFiles;
          break;
        case 'security':
        case 'service_agreement':
          sourceArray = state.securityFiles;
          break;
        case 'additional':
          sourceArray = state.additionalFiles;
          break;
        case 'archive':
          sourceArray = state.archiveFiles;
          break;
        case 'checklist':
          sourceArray = state.checklistFiles;
          break;
        default:
          console.warn(`Unknown source type: ${fromType}`);
          return;
      }
      
      // Remove file from source
      if (fromIndex !== undefined && fromIndex >= 0 && fromIndex < sourceArray.length) {
        sourceArray.splice(fromIndex, 1);
      } else {
        const fileIndex = sourceArray.findIndex(f => 
          (f.name || f.fileName) === (file.name || file.fileName)
        );
        if (fileIndex >= 0) {
          sourceArray.splice(fileIndex, 1);
        }
      }
      
      // Add file to target array
      switch (toType) {
        case 'proposal':
          targetArray = state.proposalFiles;
          targetArray.push(file);
          targetArray.sort((a, b) => {
            const dateA = extractDateFromFilename(a.name || a.fileName || '');
            const dateB = extractDateFromFilename(b.name || b.fileName || '');
            return dateB - dateA;
          });
          state.proposalFile = targetArray[0]; // Update primary
          break;
        case 'note':
          targetArray = state.noteFiles;
          targetArray.push(file);
          targetArray.sort((a, b) => {
            const dateA = extractDateFromFilename(a.name || a.fileName || '');
            const dateB = extractDateFromFilename(b.name || b.fileName || '');
            return dateB - dateA;
          });
          state.noteFile = targetArray[0]; // Update primary
          break;
        case 'security':
        case 'service_agreement':
          targetArray = state.securityFiles;
          targetArray.push(file);
          targetArray.sort((a, b) => {
            const dateA = extractDateFromFilename(a.name || a.fileName || '');
            const dateB = extractDateFromFilename(b.name || b.fileName || '');
            return dateB - dateA;
          });
          state.securityFile = targetArray[0]; // Update primary
          break;
        case 'additional':
          targetArray = state.additionalFiles;
          targetArray.push(file);
          targetArray.sort((a, b) => {
            const dateA = extractDateFromFilename(a.name || a.fileName || '');
            const dateB = extractDateFromFilename(b.name || b.fileName || '');
            return dateB - dateA;
          });
          break;
        case 'archive':
          targetArray = state.archiveFiles;
          targetArray.push(file);
          targetArray.sort((a, b) => {
            const dateA = extractDateFromFilename(a.name || a.fileName || '');
            const dateB = extractDateFromFilename(b.name || b.fileName || '');
            return dateB - dateA;
          });
          break;
        case 'checklist':
          targetArray = state.checklistFiles;
          targetArray.push(file);
          targetArray.sort((a, b) => {
            const dateA = extractDateFromFilename(a.name || a.fileName || '');
            const dateB = extractDateFromFilename(b.name || b.fileName || '');
            return dateB - dateA;
          });
          break;
        default:
          console.warn(`Unknown target type: ${toType}`);
          return;
      }
    },
    clearResults: (state) => {
      state.extractionResults = null;
      state.analysisResults = null;
    },
    clearAllFiles: (state) => {
      // Clear all file arrays
      state.proposalFiles = [];
      state.noteFiles = [];
      state.securityFiles = [];
      state.additionalFiles = [];
      state.archiveFiles = [];
      state.checklistFiles = [];
      // Clear legacy single files
      state.proposalFile = null;
      state.noteFile = null;
      state.securityFile = null;
      // Clear auto-populated files
      state.autoPopulatedFiles = null;
    },
    setExtractionResults: (state, action) => {
      state.extractionResults = action.payload;
    },
    setActiveLoanId: (state, action) => {
      state.activeLoanId = action.payload;
    },
    setExtractionResultsFromWebSocket: (state, action) => {
      console.log('🔍 DEBUG: Setting extraction results from WebSocket:', action.payload);
      state.extractionResults = action.payload;
      state.loading.extract = false;
      state.errors.extract = null;
    },
    clearErrors: (state) => {
      state.errors = {
        extract: null,
        analyze: null,
        processPDF: null,
        autoPopulate: null,
      };
    },
    
    // Preview extraction actions
    setPreviewExtractionLoading: (state, action) => {
      state.loading.previewExtraction = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Extract values
    builder
      .addCase(extractValues.pending, (state) => {
        state.loading.extract = true;
        state.errors.extract = null;
      })
      .addCase(extractValues.fulfilled, (state, action) => {
        console.log('🔍 DEBUG: extractValues.fulfilled payload:', action.payload);
        
        state.loading.extract = false;
        
        if (action.payload && action.payload.success) {
          console.log(`✅ Extraction complete: ${action.payload.analysis?.perfect_matches || 0} perfect matches`);
          console.log(`📊 Preview mode: ${action.payload.preview_mode ? 'ON' : 'OFF'}`);
          
          // Set extraction results directly from API response
          state.extractionResults = {
            extraction_result: action.payload.extraction_result,
            match_result: action.payload.match_result,
            analysis: action.payload.analysis,
            save_result: action.payload.save_result,
            loan_id: action.payload.loan_id,
            preview_mode: action.payload.preview_mode,
            documents_processed: action.payload.documents_processed,
            saved_to_database: action.payload.preview_mode ? false : true
          };
          
          // Clear any previous errors
          state.errors.extract = null;
        } else {
          console.log('🔍 DEBUG: No success in payload');
          state.errors.extract = 'Unexpected response format from server';
        }
      })
      .addCase(extractValues.rejected, (state, action) => {
        state.loading.extract = false;
        // Ensure error is a string, not an object
        state.errors.extract = typeof action.payload === 'string' 
          ? action.payload 
          : JSON.stringify(action.payload);
      })
      // Save preview results
      .addCase(savePreviewResults.pending, (state) => {
        state.loading.save = true;
        state.errors.save = null;
      })
      .addCase(savePreviewResults.fulfilled, (state, action) => {
        state.loading.save = false;
        console.log(`✅ Successfully saved ${action.payload.saved_fields} fields to database`);
        // Optionally update the extraction results to mark them as saved
        if (state.extractionResults && action.payload.success) {
          state.extractionResults.saved_to_database = true;
        }
      })
      .addCase(savePreviewResults.rejected, (state, action) => {
        state.loading.save = false;
        state.errors.save = typeof action.payload === 'string' 
          ? action.payload 
          : JSON.stringify(action.payload);
      });
    
    // Analyze documents
    builder
      .addCase(analyzeDocuments.pending, (state) => {
        state.loading.analyze = true;
        state.errors.analyze = null;
      })
      .addCase(analyzeDocuments.fulfilled, (state, action) => {
        state.loading.analyze = false;
        // Parse the nested response: {success: true, data: "JSON_STRING"}
        try {
          if (action.payload && action.payload.data) {
            state.analysisResults = JSON.parse(action.payload.data);
          } else {
            state.analysisResults = action.payload;
          }
        } catch (error) {
          console.error('Error parsing analysis results:', error);
          state.analysisResults = action.payload;
        }
      })
      .addCase(analyzeDocuments.rejected, (state, action) => {
        state.loading.analyze = false;
        // Ensure error is a string, not an object
        state.errors.analyze = typeof action.payload === 'string' 
          ? action.payload 
          : JSON.stringify(action.payload);
      });
    
    // Process PDF
    builder
      .addCase(processPDF.pending, (state) => {
        state.loading.processPDF = true;
        state.errors.processPDF = null;
      })
      .addCase(processPDF.fulfilled, (state, action) => {
        state.loading.processPDF = false;
        // Handle PDF processing result
      })
      .addCase(processPDF.rejected, (state, action) => {
        state.loading.processPDF = false;
        // Ensure error is a string, not an object
        state.errors.processPDF = typeof action.payload === 'string' 
          ? action.payload 
          : JSON.stringify(action.payload);
      });
    
    // Auto-populate files
    builder
      .addCase(autoPopulateFiles.pending, (state) => {
        state.loading.autoPopulate = true;
        state.errors.autoPopulate = null;
      })
      .addCase(autoPopulateFiles.fulfilled, (state, action) => {
        state.loading.autoPopulate = false;
        state.autoPopulatedFiles = action.payload;
        
        console.log('🔍 Auto-populate successful:', action.payload);
        
        // Store large files that need manual upload
        state.largeFiles = action.payload.large_files || [];
        
        // Reset existing auto-populated file lists to avoid duplicates on repeated runs
        state.proposalFiles = [];
        state.noteFiles = [];
        state.securityFiles = [];
        state.additionalFiles = [];
        state.archiveFiles = [];
        state.checklistFiles = [];
        state.proposalFile = null;
        state.noteFile = null;
        state.securityFile = null;

        // Auto-set files if found - match the API response property names
        if (action.payload.files) {
          // Helper to push unique paths only
          const pushUnique = (arr, filePath) => {
            const normalized = String(filePath);
            if (!arr.some(f => f.path === normalized)) {
              arr.push({
                name: normalized.split('\\').pop().split('/').pop(),
                path: normalized
              });
            }
          };
          
          // Handle proposal files
          if (action.payload.files.proposal) {
            const proposalFiles = Array.isArray(action.payload.files.proposal) 
              ? action.payload.files.proposal 
              : [action.payload.files.proposal];
            
            proposalFiles.forEach(filePath => pushUnique(state.proposalFiles, filePath));
            
            state.proposalFiles.sort((a, b) => {
              const dateA = extractDateFromFilename(a.name || '');
              const dateB = extractDateFromFilename(b.name || '');
              return dateB - dateA; // Newest first
            });
            state.proposalFile = state.proposalFiles[0]; // Set newest as primary
            console.log('🔍 Added proposal files:', proposalFiles.length);
          }
          
          // Handle note files
          if (action.payload.files.note) {
            const noteFiles = Array.isArray(action.payload.files.note) 
              ? action.payload.files.note 
              : [action.payload.files.note];
            
            noteFiles.forEach(filePath => pushUnique(state.noteFiles, filePath));
            
            state.noteFiles.sort((a, b) => {
              const dateA = extractDateFromFilename(a.name || '');
              const dateB = extractDateFromFilename(b.name || '');
              return dateB - dateA; // Newest first
            });
            state.noteFile = state.noteFiles[0]; // Set newest as primary
            console.log('🔍 Added note files:', noteFiles.length);
          }
          
          // Handle security files
          if (action.payload.files.security) {
            const securityFiles = Array.isArray(action.payload.files.security) 
              ? action.payload.files.security 
              : [action.payload.files.security];
            
            securityFiles.forEach(filePath => pushUnique(state.securityFiles, filePath));
            
            state.securityFiles.sort((a, b) => {
              const dateA = extractDateFromFilename(a.name || '');
              const dateB = extractDateFromFilename(b.name || '');
              return dateB - dateA; // Newest first
            });
            state.securityFile = state.securityFiles[0]; // Set newest as primary
            console.log('🔍 Added security files:', securityFiles.length);
          }
          
          // Handle additional files (array of unmatched files)
          if (action.payload.files.additional) {
            const additionalFiles = Array.isArray(action.payload.files.additional) 
              ? action.payload.files.additional 
              : [action.payload.files.additional];
            
            additionalFiles.forEach(filePath => pushUnique(state.additionalFiles, filePath));
            
            state.additionalFiles.sort((a, b) => {
              const dateA = extractDateFromFilename(a.name || '');
              const dateB = extractDateFromFilename(b.name || '');
              return dateB - dateA; // Newest first
            });
            console.log('🔍 Added additional files:', additionalFiles.length);
          }
          
          // Handle archive files
          if (action.payload.files.archive) {
            const archiveFiles = Array.isArray(action.payload.files.archive) 
              ? action.payload.files.archive 
              : [action.payload.files.archive];
            
            archiveFiles.forEach(filePath => pushUnique(state.archiveFiles, filePath));
            
            state.archiveFiles.sort((a, b) => {
              const dateA = extractDateFromFilename(a.name || '');
              const dateB = extractDateFromFilename(b.name || '');
              return dateB - dateA; // Newest first
            });
            console.log('🔍 Added archive files:', archiveFiles.length);
          }
          
          // Handle checklist files
          if (action.payload.files.checklist) {
            const checklistFiles = Array.isArray(action.payload.files.checklist) 
              ? action.payload.files.checklist 
              : [action.payload.files.checklist];
            
            checklistFiles.forEach(filePath => pushUnique(state.checklistFiles, filePath));
            
            state.checklistFiles.sort((a, b) => {
              const dateA = extractDateFromFilename(a.name || '');
              const dateB = extractDateFromFilename(b.name || '');
              return dateB - dateA; // Newest first
            });
            console.log('🔍 Added checklist files:', checklistFiles.length);
          }
          
          // Handle master files (if needed)
          if (action.payload.files.master) {
            state.masterFile = action.payload.files.master;
          }
        }
      })
      .addCase(autoPopulateFiles.rejected, (state, action) => {
        state.loading.autoPopulate = false;
        // Ensure error is a string, not an object
        state.errors.autoPopulate = typeof action.payload === 'string' 
          ? action.payload 
          : JSON.stringify(action.payload);
      });
    
    // Clear loan results
    builder
      .addCase(clearLoanResults.pending, (state) => {
        state.loading.deleteResults = true;
      })
      .addCase(clearLoanResults.fulfilled, (state, action) => {
        state.loading.deleteResults = false;
        // Clear the frontend state as well
        state.extractionResults = null;
        state.analysisResults = null;
      })
      .addCase(clearLoanResults.rejected, (state, action) => {
        state.loading.deleteResults = false;
      });
  },
});

// Async thunk for clearing loan results from database
export const clearLoanResults = createAsyncThunk(
  'documents/clearLoanResults',
  async (loanId, { rejectWithValue }) => {
    try {
      const response = await databaseAPI.clearLoanResults(loanId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Error clearing loan results');
    }
  }
);

export const {
  setProposalFile,
  setNoteFile,
  setSecurityFile,
  setProposalFiles,
  setNoteFiles,
  setSecurityFiles,
  setAdditionalFiles,
  setArchiveFiles,
  setChecklistFiles,
  addProposalFile,
  addNoteFile,
  addSecurityFile,
  addAdditionalFile,
  addArchiveFile,
  addChecklistFile,
  moveFile,
  clearResults,
  clearAllFiles,
  setExtractionResults,
  setActiveLoanId,
  setExtractionResultsFromWebSocket,
  clearErrors,
  setPreviewExtractionLoading,
} = documentsSlice.actions;

// Export async thunks (savePreviewResults is already exported above as createAsyncThunk)

export default documentsSlice.reducer;